import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../style/login.css";
import axios, { API_ORIGIN } from "../axios";
import "../style/Navbar.css";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  Menu,
  X,
  LogOut,
  UserRound,
  Eye,
  Share2,
  BarChart3,
  Check,
} from "lucide-react";
import Button from "./ui/Button";

const LANGS = [
  { code: "en", label: "En" },
  { code: "ar", label: "Ar" },
  { code: "ku", label: "Ku" },
];

const NAV_LINKS = [
  { to: "/en/home", label: "Home" },
  { to: "/en/about", label: "About" },
  { to: "/en/qrgenrater", label: "QR Generator" },
];

const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation().pathname;
  const [showList, setShowList] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);

  let auth = JSON.parse(localStorage.getItem("token"));
  const logout = () => {
    axios
      .post("/logout")
      .then(() => {
        localStorage.removeItem("token");
        window.location.href = "/en/home";
      })
      .catch(() => {
        localStorage.removeItem("token");
        window.location.reload();
      });
  };
  const [nav, setNav] = useState(false);
  const [lang, setLang] = useState(
    localStorage.getItem("lang") ? localStorage.getItem("lang") : "en"
  );

  const changeLanguage = (lng) => {
    localStorage.setItem("lang", lng);
    setLang(lng);
    i18n.changeLanguage(lng);
    window.location.reload();
    if (lng === "en") {
      document
        .getElementById("style")
        .setAttribute("href", "/assets/css/darkstyle" + lng + ".css");
    } else {
      document
        .getElementById("style")
        .setAttribute("href", "/assets/css/darkstyle.css");
    }
    setShowList(false);
    setMobileLangOpen(false);
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    setNav(false);
    setMenuOpen(false);
  }, [location]);

  // Close the profile dropdown on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  // The image cached in the login token goes stale once the user changes their
  // picture, so refresh it from the server whenever the route changes.
  const [avatar, setAvatar] = useState(auth?.user?.image || null);
  const [username, setUsername] = useState(auth?.user?.username || "");
  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    let cancelled = false;
    axios
      .get("/get/user")
      .then(({ data }) => {
        if (cancelled) return;
        setAvatar(data.user?.image || null);
        setUsername(data.user?.username || "");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location]);



  

  const hasAvatar = Boolean(avatar);
  const avatarSrc = hasAvatar
    ? `${API_ORIGIN}/profile/${avatar}`
    : `${process.env.PUBLIC_URL}/images/logo-only.svg`;
  // The logo fallback isn't a face photo, so it's centered on a light backdrop
  // instead of being cropped edge-to-edge like a real profile picture.
  const avatarImgClass = (size) =>
    hasAvatar
      ? `${size} rounded-full object-cover border border-ink-200`
      : `${size} rounded-full object-contain p-2 bg-gray-100 border border-ink-200`;

  const isActive = (path) => location === path;

  
  const shareProfile = async () => {
    try {
      await navigator.clipboard.writeText(`https://linkakam.com/${username}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard access can be blocked; the Share button on the setup page still offers the link.
    }
  };

  const PROFILE_ITEMS = [
    { key: "page", to: "/en/setup", icon: UserRound, label: "My page" },
    { key: "visitor", to: "/en/show", icon: Eye, label: "View as visitor" },
    {
      key: "share",
      onClick: shareProfile,
      icon: copied ? Check : Share2,
      label: copied ? "Link copied" : "Share",
    },
    { key: "stats", to: "/en/stats", icon: BarChart3, label: "Statistics" },
  ];

  const renderProfileItem = ({ key, to, onClick, icon: Icon, label }, dark) => {
    const cls = dark
      ? "flex items-center gap-3 w-full px-4 py-3 rounded-lg text-white/90 bg-white/10 text-base"
      : `flex items-center gap-3 w-full px-4 py-2.5 text-sm text-start transition-colors hover:bg-brand-50 hover:text-brand-600 ${
          to && isActive(to) ? "text-brand-600 font-semibold" : "text-ink-700"
        }`;
    const content = (
      <>
        <Icon className="w-4 h-4 shrink-0" />
        {t(label)}
      </>
    );
    return to ? (
      <Link key={key} to={to} role={dark ? undefined : "menuitem"} className={cls}>
        {content}
      </Link>
    ) : (
      <button key={key} type="button" role={dark ? undefined : "menuitem"} onClick={onClick} className={cls}>
        {content}
      </button>
    );
  };

  const LangDropdown = ({ compact }) => (
    <div className="relative">
      <button
        type="button"
        onClick={() => setShowList(!showList)}
        aria-label={t("Change language")}
        className={`flex items-center gap-1 rounded-full border border-ink-200 text-ink-600 hover:border-brand-600 hover:text-brand-600 transition-colors ${
          compact ? "px-3 py-1.5 text-sm" : "px-4 py-2 text-sm"
        }`}
      >
        {LANGS.find((l) => l.code === lang)?.label || "En"}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {showList && (
        <div
          className="absolute right-0 top-full mt-2 w-24 bg-white shadow-elevated rounded-lg overflow-hidden border border-ink-100"
          style={{ zIndex: 100 }}
        >
          <ul className="text-sm text-ink-700">
            {LANGS.map(({ code, label }) => (
              <li
                key={code}
                onClick={() => changeLanguage(code)}
                className={`px-4 py-2 cursor-pointer hover:bg-brand-50 hover:text-brand-600 ${
                  lang === code ? "font-semibold text-brand-600" : ""
                }`}
              >
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-ink-100">
        <div className="max-w-8xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link to="/en/home" className="shrink-0">
            <img
              src={`${process.env.PUBLIC_URL}/images/zz.png`}
              alt="Linkakam"
              className="w-32 md:w-36"
            />
          </Link>

          {/* Desktop nav links */}
          <nav
            aria-label={t("Main navigation")}
            className="hidden md:flex items-center gap-8 font-sans"
          >
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`text-base transition-colors ${
                  isActive(to)
                    ? "text-brand-600 font-semibold"
                    : "text-ink-600 hover:text-brand-600"
                }`}
              >
                {t(label)}
              </Link>
            ))}
          </nav>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-4">
            <LangDropdown />
            {auth ? (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label={t("Account menu")}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className="flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                >
                  <img
                    src={avatarSrc}
                    className={avatarImgClass("w-11 h-11")}
                    alt=""
                  />
                  <span className="max-w-[9rem] truncate text-sm font-semibold text-ink-900" dir="ltr">@{username}</span>
                  <ChevronDown className={`w-4 h-4 text-ink-500 transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`} />
                </button>
                <div
                  role="menu"
                  aria-hidden={!menuOpen}
                  className={`absolute right-0 rtl:right-auto rtl:left-0 top-full mt-2 w-64 bg-white shadow-elevated rounded-lg overflow-hidden border border-ink-100 origin-top-right rtl:origin-top-left transition-all duration-200 ease-out ${menuOpen ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-1 invisible pointer-events-none"}`}
                  style={{ zIndex: 100 }}
                >
                  <div className="py-1">{PROFILE_ITEMS.map((item) => renderProfileItem(item, false))}</div>
                  <div className="border-t border-ink-100 py-1">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={logout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-start text-ink-700 hover:bg-accent-50 hover:text-accent-600 transition-colors"
                    >
                      <LogOut className="w-4 h-4 shrink-0" />
                      {t("Logout")}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Button to="/en/setup" variant="primary" size="md">
                {t("Login")}
              </Button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setNav(!nav)}
            aria-label={t("Open menu")}
            className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center text-ink-700"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile nav overlay */}
      <div
        aria-hidden={!nav}
        className={`md:hidden fixed inset-0 bg-ink-900 flex flex-col transition-all duration-300 ease-out ${nav ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 invisible pointer-events-none"}`}
        style={{ zIndex: 990 }}
      >
        <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
          <img
            src={`${process.env.PUBLIC_URL}/images/zz.png`}
            alt="Linkakam"
            className="w-32"
          />
          <button
            onClick={() => setNav(false)}
            aria-label={t("Close menu")}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-10 flex flex-col gap-8">
          <nav className="flex flex-col gap-6 text-2xl font-display">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={isActive(to) ? "text-white font-bold" : "text-ink-300"}
              >
                {t(label)}
              </Link>
            ))}
          </nav>

          <div className="mt-auto flex flex-col gap-4">
            {auth && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 pb-3">
                  <img
                    src={avatarSrc}
                    className={
                      hasAvatar
                        ? "w-12 h-12 rounded-full object-cover border border-white/20"
                        : "w-12 h-12 rounded-full object-contain p-2 bg-gray-100 border border-white/20"
                    }
                    alt=""
                  />
                  <p className="min-w-0 text-white font-semibold truncate" dir="ltr">
                    @{username}
                  </p>
                </div>
                {PROFILE_ITEMS.map((item) => renderProfileItem(item, true))}
              </div>
              )}

              <div className={`relative ${auth ? "pt-4 border-t border-white/10" : ""}`}>
                <button
                  type="button"
                  onClick={() => setMobileLangOpen(!mobileLangOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-white/10 text-white text-base"
                >
                  {t("Language")}: {LANGS.find((l) => l.code === lang)?.label || "En"}
                  <ChevronDown className="w-4 h-4" />
                </button>
                {mobileLangOpen && (
                  <div className="mt-2 rounded-lg overflow-hidden bg-white">
                    <ul className="text-ink-800 text-base">
                      {LANGS.map(({ code, label }) => (
                        <li
                          key={code}
                          onClick={() => changeLanguage(code)}
                          className={`px-4 py-3 cursor-pointer hover:bg-brand-50 ${
                            lang === code ? "font-semibold text-brand-600" : ""
                          }`}
                        >
                          {label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {auth ? (
                <Button
                  as="button"
                  variant="outline"
                  size="lg"
                  className="!text-white !border-white/30 hover:!border-white justify-center"
                  onClick={logout}
                >
                  {t("Logout")}
                  <LogOut className="w-4 h-4" />
                </Button>
              ) : (
                <Button to="/en/setup" variant="primary" size="lg" className="justify-center">
                  {t("Login")}
                </Button>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default Navbar;
